import { MutableRefObject, RefObject, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { widgetHoverAtom } from "atoms/interaction";
import {
  IS_MOSAIC_CHART,
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART
} from "constants/chart.constants";
import { useChartsContext } from "contexts/ChartContext";
import { useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { selectChartHistogramToggleFeature } from "store/features";
import { RootState } from "store/rootReducer";

import { getAxisFieldSelection } from "components/charts/hooks/useAxisFieldSelection";

import { useChartEntities } from "../useChartEntities";

export type AxisKey = "xAxis" | "yAxis";
/** A bare key covers every axis under it; `index` narrows to one of them. */
export type AxisTitleTarget = AxisKey | { key: AxisKey; index: number };

const AXIS_KEYS: AxisKey[] = ["xAxis", "yAxis"];

const isTarget = (targets: AxisTitleTarget[], key: AxisKey, index: number) =>
  targets.some((t) =>
    typeof t === "string" ? t === key : t.key === key && t.index === index
  );

const targetsKey = (key: AxisKey, targets: AxisTitleTarget[]) =>
  targets.some((t) => (typeof t === "string" ? t : t.key) === key);

const asArray = (axis: unknown): GenericObject[] =>
  Array.isArray(axis) ? axis : axis ? [axis as GenericObject] : [];

const parseOptions = (options: Maybe<string>): GenericObject => {
  try {
    return JSON.parse(options || "{}") ?? {};
  } catch {
    return {};
  }
};

/** Blanks the canvas title of every targeted axis. */
export function blankAxisNames<T extends object>(
  options: T,
  targets: AxisTitleTarget[]
): T {
  const blanked: GenericObject = { ...(options as GenericObject) };
  for (const key of AXIS_KEYS) {
    const axis = blanked[key] as Maybe<GenericObject | GenericObject[]>;
    if (!axis || !targetsKey(key, targets)) continue;
    const blank = (a: GenericObject, i: number) =>
      isTarget(targets, key, i) ? { ...a, name: "" } : a;
    blanked[key] = Array.isArray(axis) ? axis.map(blank) : blank(axis, 0);
  }
  return blanked as T;
}

/**
 * Refills titles blanked by blankAxisNames from the persisted (never blanked)
 * axis options, matched by index.
 */
export function restoreAxisNames<T extends object>(
  options: T,
  persistedOptions: Maybe<string>
): T {
  const persisted = parseOptions(persistedOptions);
  const restored: GenericObject = { ...(options as GenericObject) };
  for (const key of AXIS_KEYS) {
    const source = asArray(persisted[key]);
    const axis = restored[key] as Maybe<GenericObject | GenericObject[]>;
    if (!axis || source.length === 0) continue;
    const fill = (a: GenericObject, i: number) =>
      a?.name === "" && source[i]?.name ? { ...a, name: source[i].name } : a;
    restored[key] = Array.isArray(axis) ? axis.map(fill) : fill(axis, 0);
  }
  return restored as T;
}

/**
 * The axes whose titles sit under a hover field picker, and whether they are
 * hidden right now. Every hook that draws the axes blanks these names while
 * `hideTitles`, or a rebuild mid-hover redraws them under the pickers.
 */
export function useAxisTitleTargets() {
  const { id } = useChartsContext();
  const { chartType, jitterPlotToggle, screenshot, dataTable } = useChartEntities(id);
  const isHistogramOn = useSelector((state: RootState) =>
    selectChartHistogramToggleFeature(state, id)
  );
  // Narrowed to this chart so other widgets' hover changes don't re-render it.
  const isHoverAtom = useMemo(
    () => selectAtom(widgetHoverAtom, (m) => m[id] ?? false),
    [id]
  );
  const isHover = useAtomValue(isHoverAtom);

  const pickers = getAxisFieldSelection(chartType, jitterPlotToggle?.active);
  const isHorizontalStackedBar = IS_STACKED_BAR_HORIZONTAL_CHART(chartType);
  // Histogram mode hides the stacked bar category picker, so that title stays.
  const hiddenCategoryAxis: Maybe<AxisKey> =
    isHistogramOn?.active && (IS_STACKED_BAR_CHART(chartType) || isHorizontalStackedBar)
      ? isHorizontalStackedBar
        ? "yAxis"
        : "xAxis"
      : null;
  const titleAxes: AxisTitleTarget[] = AXIS_KEYS.filter(
    (key) => pickers[key === "xAxis" ? "x" : "y"] && key !== hiddenCategoryAxis
  ).map((key) =>
    // Mosaic's top x-axis carries the count title, not a picker.
    IS_MOSAIC_CHART(chartType) && key === "xAxis" ? { key, index: 0 } : key
  );
  const hideTitles =
    titleAxes.length > 0 && isHover && !screenshot?.active && !dataTable?.active;

  return { titleAxes, hideTitles };
}

/**
 * Hides the canvas titles of `axes` while the hover buttons that sit on them are
 * shown, so the two are never visible together (EVA-407). Toggles with a
 * merge-only name patch rather than a rebuild, so zoom and series state survive.
 * Option builders must also blank these names while `hidden`, or a rebuild
 * mid-hover redraws them. No-op in screenshot mode, which owns the titles.
 */
export function useHideAxisTitles(
  chartInstanceRef:
    | RefObject<Nullable<echarts.ECharts>>
    | MutableRefObject<Nullable<echarts.ECharts>>,
  hidden: boolean,
  axes: AxisTitleTarget[],
  persistedOptions: Maybe<string>,
  screenshotActive: boolean
) {
  // Read at toggle time only: persisted options change on every rebuild, which
  // already blanks/keeps the names itself.
  const persistedRef = useRef(persistedOptions);
  persistedRef.current = persistedOptions;
  const axesKey = axes
    .map((t) => (typeof t === "string" ? t : `${t.key}.${t.index}`))
    .join(",");

  useEffect(() => {
    if (screenshotActive || axes.length === 0) return;
    const instance = chartInstanceRef.current;
    if (!instance || instance.isDisposed()) return;
    // Sized from the live chart: the persisted JSON can still describe the
    // previous chart type, and merging extra entries would add axes.
    const live = (instance.getOption() ?? {}) as GenericObject;
    const persisted = parseOptions(persistedRef.current);
    const patch: GenericObject = {};
    for (const key of AXIS_KEYS) {
      if (!targetsKey(key, axes)) continue;
      const source = asArray(persisted[key]);
      // Restore fills blanked names only, so titles a rebuild already redrew
      // are never overwritten with stale ones.
      const entries = asArray(live[key]).map((a, i) =>
        !isTarget(axes, key, i)
          ? {}
          : hidden
          ? { name: "" }
          : a?.name === "" && source[i]?.name
          ? { name: source[i].name }
          : {}
      );
      if (entries.some((e) => "name" in e)) patch[key] = entries;
    }
    if (Object.keys(patch).length === 0) return;
    instance.setOption(patch);
    // axes is compared by axesKey; the ref is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, axesKey, screenshotActive]);
}
